/**
 * Brazilian phone formatting for the signature form.
 *
 * The signature is pasted into an e-mail client, so the number is read by people, not parsed by
 * anything — and an unformatted `11916610024` in the middle of a signature block reads as a typo.
 *
 * Formats as the person types and keeps only digits underneath, so what is stored is consistent
 * no matter how it was pasted.
 *
 * @module Pages/Signatures
 */

/** Digits in a mobile number with area code: 2 + 9. */
const MOBILE_LENGTH = 11;

/**
 * `11916610024` → `(11) 91661-0024`, formatting partial input as it grows.
 *
 * Landlines (10 digits) come out as `(11) 5505-4750`: the break moves with the length instead of
 * forcing a 9th digit that is not there.
 *
 * Anything longer than 11 digits is truncated — a pasted `+55` prefix would otherwise push the
 * real number out of the mask.
 */
export function formatBrazilianPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, MOBILE_LENGTH);

  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;

  const area = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (rest.length <= 4) return `(${area}) ${rest}`;

  // 9-digit mobiles break after 5, 8-digit landlines after 4.
  const breakAt = rest.length > 8 ? 5 : 4;
  return `(${area}) ${rest.slice(0, breakAt)}-${rest.slice(breakAt)}`;
}

/** Whether the number has enough digits to be a real landline or mobile. */
export function isCompleteBrazilianPhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || digits.length === MOBILE_LENGTH;
}
